import { makeStyles, Paper } from "@material-ui/core";
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { deleteMessage, getDrafts, getDrivers } from "../../actions/messageCenterActions";
import { getUserPreference } from "../../utilities/common";
import MessageDetail from "./MessageDetail";
import moment from "moment";
import _ from 'lodash';
import { useToken } from "../../utilities/hooks";
import UniDialog from "../Common/UniDialog";
import { showAlertSnackbar } from "../../reducers/uiSlice";
import FullscreenLoading from "../Common/FullscreenLoading";
import { DataGrid } from "@material-ui/data-grid";

const useStyles = makeStyles((theme)=> ({
    //  bg
    //  bg-highlight
    root: {
        backgroundColor: '#343434',
        border: '1px solid #9e9e9e',
        padding: 20,
        marginBottom: 15,
        borderRadius: 0
    },
    titleGroup: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        fontWeight: 800,
        fontSize: 18
    },
    iconStyle: {
        marginLeft: 10,
        cursor: 'pointer'
    },
    infoStyle: {
        display: 'flex',
        color: theme.palette.grey[500],
        fontSize: 14,
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    detailInfo: {
        marginRight: 10,
        display: 'flex'
    },
    contentStyle: {
        marginTop: 20,
        fontSize: 16,
        width: '100%',
        wordWrap: 'break-word'
    },
    detailNameInfo: {
        marginRight: 5
    }
}))

function MessageOverview(props) {
    const classes = useStyles();
    const {t} = useTranslation('messageCenter');
    const dispatch = useDispatch();
    const userPref = getUserPreference();
    const tokenContents = useToken();

    const warehouses = tokenContents.warehouse;

    const messageTypes = useSelector(state => state.messageCenterReducer.getMessageTypes);

    const DETAIL_INIT = {
        open: false,
        type: 'edit',
        message: {}
    }

    const columns = [
        {field: 'id', headerName: t('driver_id'), width: 100, headerAlign: 'center', align: 'center', flex: 1},
        {field: 'phone', headerName: t('phone'), width: 200, headerAlign: 'center', sortable: false, align: 'center', flex: 1},
        {field: 'lastname', headerName: t('last_name'), width: 150, headerAlign: 'center', align: 'center', flex: 1},
        {field: 'firstname', headerName: t('first_name'), width: 150, headerAlign: 'center', sortable: false, align: 'center', flex: 1}
    ];

    const [messageType, setMessageType] = useState();
    const [recipients, setRecipients] = useState();
    const [detailParams, setDetailParams] = useState(DETAIL_INIT);
    const [warehouseAuth, setWarehouseAuth] = useState(true);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quantityDetailOpen, setQuantityDetailOpen] = useState(false);
    const [quantityDetail, setQuantityDetail] = useState([]);

    const handleDelete = async() => {
        try {
            setLoading(true);
            await dispatch(deleteMessage(props.message.id, {operator: userPref.username, warehouse: userPref.userCity}));
            if(props.type === 'drafts') {
                await dispatch(getDrafts({warehouse: userPref.userCity}));
            } else {
                await props.handleSearch();
            }
            await dispatch(showAlertSnackbar({message:t('delete_success'), type: 'success'}))
        } catch {
            dispatch(showAlertSnackbar({message:t('delete_err'), type: 'error'}))
        } finally {
            setLoading(false);
        }
    }

    const handleDetail = (type) => {
        setDetailParams({
            open: true,
            type: type,
            message: props.message
        })
    }

    const handleDriverDetailOpen = () => {
        if(props.message.unread <= 0) {
            return;
        }
        setLoading(true);
        setQuantityDetail([]);
        dispatch(getDrivers(props.message.id))
        .then((res) => {
            setQuantityDetail(res);
            setQuantityDetailOpen(true);
        })
        .catch(() => {
            dispatch(showAlertSnackbar({message: t('fetch_error'), type: 'error'}))
        })
        .finally(() => {
            setLoading(false);
        })
    }

    const createRows = () => {
        if(quantityDetail?.length > 0) {
            return quantityDetail.map((row) => {
                const { driver_id, ...rest } = row
                return {
                    id: driver_id,
                    ...rest
                }
            })
        }
        return [];
    }

    useEffect(() => {
        messageTypes.forEach((message => {
            if(message.code + '' === props.message.message_type + '') {
                setMessageType(message.name)
                return ;
            }
        }))
        setRecipients(_.map(props.message.recipients, 'name').toString());
        // Warehouses auth
        if(props.message.recipient_type + '' === '1') {
            props.message.recipients.forEach((warehouse) => {
                if(!warehouses.includes(Number(warehouse.id))) {
                    setWarehouseAuth(false);
                    return;
                }
            })
        } else {
            setWarehouseAuth(true);
        }
    }, [props.message])

    return (
        <Paper className={classes.root}>
            <div className={classes.titleGroup}>
                <div 
                    style={{
                        width: '90%',
                        wordWrap: 'break-word'
                    }}
                >
                    <span>{props.message.title}</span>
                    {
                        messageType === 'Announcement' &&
                        props.type !== 'drafts' &&
                        (
                            Number(props.message.unread) <= 0 ?
                            <span 
                                style={{
                                    color: 'grey',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    marginLeft: 20
                                }}
                            >
                                {t('all_read')}
                            </span>
                            :
                            <span 
                                style={{
                                    color: 'orange',
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    marginLeft: 20,
                                    cursor: 'pointer'
                                }}
                                onClick={handleDriverDetailOpen}
                            >
                                {props.message.unread}/{props.message.total} {t('unread')}
                            </span>
                        )
                    }
                </div>
                <div>
                    {
                        warehouseAuth ? 
                        <FileCopyIcon 
                            color='primary' 
                            className={classes.iconStyle} 
                            onClick={()=>handleDetail('copy')}
                        />
                        :
                        <FileCopyIcon
                            style={{color:'grey',marginLeft: 10}}
                        />
                    }
                    {
                        props.type !== 'history' && (
                            warehouseAuth ? 
                            <EditIcon 
                                color='primary'
                                className={classes.iconStyle} 
                                onClick={()=>handleDetail('edit')}
                            />
                            :
                            <EditIcon 
                                style={{color:'grey',marginLeft: 10}}
                            />
                        )
                    }
                    {
                        props.type !== 'history' && (
                            warehouseAuth ? 
                            <DeleteIcon 
                                color='primary' 
                                className={classes.iconStyle}
                                onClick={() => setDeleteConfirmOpen(true)}
                            />
                            :
                            <DeleteIcon 
                                style={{color:'grey',marginLeft: 10}}
                            />
                        )
                    }
                </div>
            </div>
            <div className={classes.infoStyle}>
                <div className={classes.detailInfo}>
                    <span className={classes.detailNameInfo}>{t('type')}</span>
                    <span>{messageType}</span>
                </div>
                <div className={classes.detailInfo}>
                    <span className={classes.detailNameInfo}>{t('recipient')}</span>
                    <span 
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 900
                        }}
                    >
                        {recipients}
                    </span>
                </div>
                <div className={classes.detailInfo}>
                    <span className={classes.detailNameInfo}>{t('post_date')}</span>
                    <span>{props.message.published_at || 'NULL'}</span>
                </div>
                <div className={classes.detailInfo}>
                    <span className={classes.detailNameInfo}>{t('effective_date')}</span>
                    <span className={classes.detailNameInfo}>{props.message.effective_from}</span>
                    <span className={classes.detailNameInfo}>{t('to')}</span>
                    <span>{props.message.effective_to}</span>
                </div>
            </div>
            <div className={classes.contentStyle}>{props.message.content}</div>
            <MessageDetail 
                open={detailParams.open}
                detail={detailParams.message}
                type={detailParams.type}
                onClose={() => setDetailParams(DETAIL_INIT)}
                messageGeneralType={props.type}
            />
            <UniDialog 
                open={deleteConfirmOpen}
                title={t('delete_message')}
                message={t('confirm_delete_message')}
                fullWidth
                onClose={() => {
                  setDeleteConfirmOpen(false);
                }}
                onFinish={() => {
                    handleDelete();
                }}
            >
            </UniDialog>
            <UniDialog
                open={quantityDetailOpen}
                onClose={() => {
                  setQuantityDetailOpen(false);
                }}
                fullWidth
                title={t('unread_driver')}
                disableDefaultButtons
            >
                <div style={{paddig: 20}}>
                    <DataGrid
                        alignItems="center"
                        rows={createRows()}
                        columns={columns}
                        disableColumnFilter 
                        disableColumnMenu
                        pageSize={10}
                        isRowSelectable={false}
                        fullWidth
                        style={{height: '50vh'}}
                    />
                </div>
            </UniDialog>
            <FullscreenLoading open={loading} />
        </Paper>
    )
}
export default MessageOverview;